import { Image } from "@heroui/react";
import { useEnvContext } from "context/EnvContext";
import { DOCUMENT_PRIVACY_POLICY } from "core/consts";
import {
  ENV_DEMO,
  getStaticAsset,
  isReceiverApp,
  isSenderApp,
} from "core/model/config";
import { useExternalLinkLabel } from "ds_legacy/components/LinkV2";
import { getRecareLogoWithNamePath } from "ds_legacy/components/RecareLogo";
import { Banner, Button, ConnectedPasswordInput, Link } from "ds/ui";
import { useLegalDocuments } from "dsl/atoms/LegalDocuments";
import { useMedia } from "dsl/atoms/ResponsiveMedia";
import { loginPage, LoginPageVariants } from "dsl/ecosystems/LoginPage/classes";
import {
  useAcpHandlerUrls,
  useCareproviderHandlerUrls,
  useCareseekerHandlerUrls,
} from "dsl/hooks/useNavigationHandlers";
import { validatePassword } from "dsl/organisms/PasswordResetPageV2";
import {
  composeValidation,
  convertModelDefinition,
  SimpleFormRenderProps,
  ValidationFunction,
  valueDef,
} from "react-forms-state/src";
import { useTranslations } from "translations";
import Translations from "translations/types";
import { PasswordChangeSuccessPage } from "./PasswordChangeSuccessPage";
import {
  isPasswordChangeDefaultView,
  PasswordChangeErrorState,
  useChangePassword,
} from "./useChangePassword";

export type PasswordChangePageProps = LoginPageVariants & {
  errors?: PasswordChangeErrorState;
  goToDashboardUrl: string;
  goToProductionUrl: string;
  isLoading: boolean;
  onSubmit: () => void;
  resetError: () => void;
};

export const validateCurrentPassword: ValidationFunction = (
  { current_password }: { current_password: string | null },
  { translations }: { translations: Translations },
): true | Partial<Record<"current_password", string>> => {
  let validation: true | Partial<Record<"current_password", string>> = true;

  // Check if the password is missing
  if (!current_password) {
    validation = {
      current_password: translations.login.loginPage.currentPasswordValidation,
    };
  }

  return validation;
};

export function PasswordChangePage({
  app,
  env,
  errors,
  goToDashboardUrl,
  goToProductionUrl,
  isLoading,
  onSubmit,
  resetError,
}: PasswordChangePageProps) {
  const isDemo = env === ENV_DEMO;
  const classes = loginPage({ app, env });
  const { isMobile } = useMedia();
  const translations = useTranslations();
  const { getDocumentUrl } = useLegalDocuments();
  const externalLinkLabel = useExternalLinkLabel();
  return (
    <div className={classes.page()}>
      <div className={classes.container()}>
        <div className={classes.content()}>
          <div className={classes.header()}>
            <Image
              alt="Recare logo"
              src={getStaticAsset(getRecareLogoWithNamePath("master"))}
              width={160}
            />
          </div>
          <div className={classes.form()}>
            <div className={classes.formContent()}>
              <h1 className={classes.title()}>
                {isDemo && (
                  <span className="font-normal">
                    {translations.login.loginPage.titleDemo} |{" "}
                  </span>
                )}
                {translations.login.loginPage.titleChangePassword}
              </h1>
              <p className="text-sm" id="description">
                {translations.login.setNewPasswordInstructions}
              </p>
              {isDemo && (
                <Banner
                  color="warning"
                  message={
                    <p>
                      {translations.login.loginPage.environmentInfo}{" "}
                      <Link
                        aria-label={externalLinkLabel(
                          translations.login.loginPage.productionLink,
                          "website",
                        )}
                        color="primary-dark"
                        href={goToProductionUrl}
                        id="go-to-prod"
                        size="sm"
                      >
                        {translations.login.loginPage.productionLink}
                      </Link>
                      .
                    </p>
                  }
                  size="sm"
                />
              )}
            </div>
            <div className={classes.formContent()}>
              {errors?.general && (
                <Banner
                  color="danger"
                  data-testid="password-change-error"
                  message={errors.general}
                  role="alert"
                />
              )}
              <ConnectedPasswordInput
                elementName="current_password"
                errorMessage={errors?.current_password}
                isInvalid={!!errors?.current_password}
                label={translations.login.currentPassword}
                placeholder={translations.login.loginPage.passwordPlaceholder}
                required
                sideMutation={() => resetError()}
              />
              <ConnectedPasswordInput
                aria-describedby="description"
                elementName="new_password"
                label={translations.login.enterNewPasswordTitle}
                placeholder={translations.login.enterNewPasswordPlaceholder}
                required
                sideMutation={() => resetError()}
                withIndicator
              />
              <ConnectedPasswordInput
                elementName="confirm_new_password"
                label={translations.login.confirmYourPasswordTitle}
                placeholder={translations.login.confirmNewPasswordPlaceholder}
                required
                sideMutation={() => resetError()}
              />
            </div>
            <div className="flex flex-col-reverse items-center justify-between gap-y-2 tablet:flex-row">
              <Link
                color="primary-dark"
                href={goToDashboardUrl}
                id="go-to-dashboard"
              >
                {translations.login.loginPage.toDashboardLink}
              </Link>
              <Button
                color="primary"
                fullWidth={isMobile}
                id="save-new-password"
                isLoading={isLoading}
                size="lg"
                type="submit"
                variant="solid"
                onPress={onSubmit}
              >
                {translations.login.loginPage.saveNewPasswordButton}
              </Button>
            </div>
            <Banner
              color="warning"
              message={translations.login.setNewPasswordEncryptionInformation}
              size="sm"
            />
          </div>
        </div>
        <div className={classes.footer()}>
          <hr className={classes.footerDivider()} />
          <p>
            {translations.login.loginPage.privacyPolicyInfo}{" "}
            <Link
              aria-label={externalLinkLabel(
                translations.login.privacyPolicy,
                "pdf",
              )}
              color="primary-dark"
              href={getDocumentUrl(DOCUMENT_PRIVACY_POLICY)}
              id="privacy-policy"
              isExternal
              size="sm"
            >
              {translations.login.privacyPolicy}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default function ConnectedPasswordChangePage() {
  const { app, env } = useEnvContext();
  const { changePasswordView, errors, handleSubmit, isLoading, resetError } =
    useChangePassword();
  const { goToDashboard, goToProductionEnv: goToProductionEnvSender } =
    useCareseekerHandlerUrls();
  const { goToProductionEnv: goToProductionEnvReceiver, goToRequestDashboard } =
    useCareproviderHandlerUrls();
  const {
    goToDashboard: goToDashboardAcp,
    goToProductionEnv: goToProductionEnvAcp,
  } = useAcpHandlerUrls();

  return (
    <main>
      {isPasswordChangeDefaultView(changePasswordView) ? (
        <SimpleFormRenderProps
          asHtmlForm
          modelDefinition={convertModelDefinition({
            ...valueDef("current_password"),
            ...valueDef("new_password"),
            ...valueDef("confirm_new_password"),
          })}
          validate={composeValidation(
            validatePassword,
            validateCurrentPassword,
          )}
          onSubmit={handleSubmit}
        >
          {({ submit }) => (
            <PasswordChangePage
              app={app as LoginPageVariants["app"]}
              env={env}
              errors={errors}
              goToDashboardUrl={
                isSenderApp(app)
                  ? goToDashboard()
                  : isReceiverApp(app)
                  ? goToRequestDashboard()
                  : goToDashboardAcp()
              }
              goToProductionUrl={
                isSenderApp(app)
                  ? goToProductionEnvSender()
                  : isReceiverApp(app)
                  ? goToProductionEnvReceiver()
                  : goToProductionEnvAcp()
              }
              isLoading={isLoading}
              resetError={resetError}
              onSubmit={submit}
            />
          )}
        </SimpleFormRenderProps>
      ) : (
        <PasswordChangeSuccessPage
          app={app as LoginPageVariants["app"]}
          env={env}
          loginUrl="/logout?reason=password_change"
        />
      )}
    </main>
  );
}
